import { z } from 'zod';
import {
  latitudeValidator,
  longitudeValidator,
  nonNegativeIntValidator,
} from '../common/validators';

/**
 * Address schema
 * Represents a shipping or billing address
 */
export const AddressSchema = z.object({
  /** Address ID */
  id: nonNegativeIntValidator,
  /** Full name associated with address */
  fullName: z.string().max(255).nullable(),
  /** User ID who owns this address */
  userId: nonNegativeIntValidator,
  /** Country ID */
  countryId: nonNegativeIntValidator,
  /** Address status (active/inactive) */
  status: z.number().int().min(0).max(255).default(1),
  /** Whether this is the default address */
  isDefault: z.number().int().min(0).max(1).nullable(),
  /** City/locality */
  locality: z.string().max(255),
  /** State/region/province */
  region: z.string().max(255),
  /** Address line 1 (street address) */
  line1: z.string().max(255),
  /** Address line 2 (apartment, suite, etc.) */
  line2: z.string().max(255).nullable(),
  /** Postal/ZIP code */
  zipCode: z.string().max(20),
  /** Longitude coordinate */
  longitude: longitudeValidator,
  /** Latitude coordinate */
  latitude: latitudeValidator,
});

export type Address = z.infer<typeof AddressSchema>;

/**
 * Address display schema - for Address component props
 * A simplified schema for displaying addresses in the UI
 */
export const AddressDisplaySchema = z.object({
  id: z.number().int().positive(),
  fullName: z.string().min(1),
  line1: z.string().min(1),
  line2: z.string().nullable().optional(),
  locality: z.string().min(1),
  region: z.string().min(1),
  zipCode: z.string().min(1),
  countryId: z.number().int().positive(),
  isDefault: z.union([z.literal(0), z.literal(1)]),
  latitude: z.number().nullable().optional(),
  longitude: z.number().nullable().optional(),
});

export type AddressDisplay = z.infer<typeof AddressDisplaySchema>;
